
const libPictView = require('pict-view');
const libTabBar = require('./TabBar.js');

/**
 * Access & Permissions workspace.
 *
 * A single cohesive screen for the rich RBAC layer (Roles, Permission Groups, Users) that replaces the raw
 * Role / RolePermission / RoleEdge / UserRoleJoin recordset lists. A rail of subjects on the left, a
 * workspace-global context bar up top, and a tabbed editor on the right. Thin view: all behavior + state live
 * in the AccessWorkspace provider; this derives + repaints declaratively (the MasterListManager idiom).
 *
 * Phase 0 ships the shell + the Details tab; the Permissions matrix, Effective visualizer, Composition,
 * Members, assignment and overrides tabs are filled by later phases.
 */
class AccessWorkspace extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.AccessWorkspace;
	}

	deriveRenderState()
	{
		if (this.provider)
		{
			this.provider.deriveRenderState();
		}
	}

	onBeforeRender(pRenderable)
	{
		this.deriveRenderState();
		// A full re-render destroys + recreates the live-filter inputs (rail + matrix), dropping focus.
		// Remember the focused input (by id) + caret here so onAfterRender can restore it (see below).
		this._focusMemo = null;
		try
		{
			if (typeof document !== 'undefined' && document.activeElement)
			{
				const tmpActive = document.activeElement;
				if (tmpActive.id && (tmpActive.tagName === 'INPUT' || tmpActive.tagName === 'TEXTAREA'))
				{
					this._focusMemo = { id: tmpActive.id, start: tmpActive.selectionStart, end: tmpActive.selectionEnd };
				}
			}
		}
		catch (pError)
		{
			this._focusMemo = null;
		}
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		libTabBar.layout(document);
		// Restore focus + caret to the input that had it before this re-render, so typing in a live filter
		// stays fluid.
		try
		{
			if (this._focusMemo && typeof document !== 'undefined')
			{
				const tmpInput = document.getElementById(this._focusMemo.id);
				if (tmpInput)
				{
					tmpInput.focus();
					if (this._focusMemo.start !== null && this._focusMemo.start !== undefined && tmpInput.setSelectionRange)
					{
						try
						{
							tmpInput.setSelectionRange(this._focusMemo.start, this._focusMemo.end);
						}
						catch (pCaretError)
						{/* some input types don't support selection range */}
					}
				}
			}
		}
		catch (pError)
		{
			// non-fatal: focus restoration is a nicety
		}
		// Mount the "add users" multi-pickers (no-ops unless their mount is in the DOM): the Members tab's and the
		// Details (View/Create Role) form's.
		if (this.pict.providers.GroupComposition) { this.pict.providers.GroupComposition.mountAddMembersPicker(); }
		if (this.pict.providers.UserGroupGrants) { this.pict.providers.UserGroupGrants.renderGrantsHost(); }
		if (this.pict.providers.AccessWorkspace) { this.pict.providers.AccessWorkspace.mountDetailsAddMembersPicker(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = AccessWorkspace;

module.exports.default_configuration =
{
	ViewIdentifier: 'AccessWorkspace',
	DefaultRenderable: 'AccessWorkspace-Wrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		${ libTabBar.TABBAR_CSS }
		.acw-wrap { padding: 1.25rem 1.5rem 2rem; }
		.acw-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 1rem; flex-wrap: wrap; }
		.acw-context { display: flex; align-items: center; }
		.acw-context .field { margin-bottom: 0; }
		.acw-context-label { display: inline-flex; align-items: center; height: 2.5em; padding: 0 0.6rem; font-size: 0.75rem; font-weight: 700;
			text-transform: uppercase; letter-spacing: 0.04em; color: var(--bulma-text-weak, #7a7a7a); background: var(--bulma-background, #f5f5f5); border-radius: 4px 0 0 4px; }
		.acw-rail { padding: 0.6rem; }
		.acw-search { margin-bottom: 0.6rem; }
		.acw-rail-group { margin-bottom: 0.75rem; }
		.acw-rail-head { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--bulma-text-weak, #7a7a7a); padding: 0.35rem 0.4rem; display: flex; align-items: center; gap: 0.4rem; }
		.acw-rail-count { margin-left: auto; font-weight: 600; color: var(--bulma-text-weak, #9a9a9a); }
		.acw-rail-row { display: flex; flex-direction: column; gap: 0.05rem; padding: 0.4rem 0.55rem; border-radius: 6px; cursor: pointer; color: inherit; }
		.acw-rail-row:hover { background: var(--bulma-background, #f5f5f5); }
		.acw-rail-row.is-active { background: var(--theme-color-brand-primary, #2e7d74); }
		.acw-rail-row.is-active .acw-rail-label, .acw-rail-row.is-active .acw-rail-sub { color: #fff; }
		.acw-rail-label { font-weight: 600; }
		.acw-rail-sub { font-size: 0.72rem; color: var(--bulma-text-weak, #9a9a9a); }
		.acw-subject { padding: 1.1rem 1.25rem 1.5rem; }
		.acw-subject-head { display: flex; align-items: center; gap: 0.8rem; margin-bottom: 0.5rem; }
		.acw-subject-head .title { margin-bottom: 0.1rem; }
		.acw-subject-kind { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--bulma-text-weak, #7a7a7a); font-weight: 700; }
		.acw-tabs { margin-bottom: 1rem; }
		.acw-tab-body { min-height: 8rem; }
		.acw-details th.acw-detail-label { width: 14rem; color: var(--bulma-text-weak, #5a5a5a); font-weight: 600; }
		.acw-placeholder { padding: 2.5rem 1rem; border: 1px dashed var(--bulma-border, #dbdbdb); border-radius: 8px; }
		.acw-empty { padding: 3rem 1rem; }
		.acw-grants .pw-help { font-size: 0.85rem; color: var(--theme-color-text-muted, #6b7686); }
		.acw-grant-section { margin: 0 0 1.1rem; }
		.acw-grant-head { display: flex; align-items: baseline; gap: 0.5rem; font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-secondary, #45505f); padding: 0 0.1rem 0.35rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f6); margin-bottom: 0.35rem; }
		.acw-grant-count { font-weight: 700; color: var(--theme-color-text-muted, #98a2b3); }
		.acw-grant-empty { font-size: 0.85rem; color: var(--theme-color-text-muted, #98a2b3); padding: 0.3rem 0.1rem; }
		.acw-grant-row { display: flex; align-items: center; gap: 0.55rem; padding: 0.4rem 0.2rem; border-bottom: 1px solid var(--theme-color-border-light, #f4f6f9); }
		.acw-grant-cicon { color: var(--theme-color-text-muted, #9aa3af); flex: 0 0 auto; }
		.acw-grant-name { font-weight: 600; color: var(--theme-color-text-primary, #1f2733); }
		.acw-grant-id { font-size: 0.7rem; color: var(--theme-color-text-muted, #9aa3af); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.acw-grant-srcs { margin-left: auto; display: inline-flex; flex-wrap: wrap; gap: 0.3rem; justify-content: flex-end; }
		.acw-grant-src { font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; padding: 0.08rem 0.42rem; border-radius: 9px; }
		.acw-grant-src-direct { color: #276749; background: #e6f4ea; }
		.acw-grant-src-via { color: #45505f; background: var(--theme-color-background-secondary, #eef1f6); }
		.acw-grant-src-more { color: #5650e6; background: #ecebfb; cursor: default; }
		.acw-grant-x { flex: 0 0 auto; border: none; background: transparent; color: var(--theme-color-text-muted, #98a2b3); cursor: pointer; padding: 0.1rem 0.3rem; border-radius: 5px; line-height: 1; }
		.acw-grant-x:hover { color: var(--theme-color-status-danger, #c0392b); background: #fdecec; }
		.acw-grant-add { margin-top: 1.1rem; padding-top: 0.9rem; border-top: 1px solid var(--theme-color-border-light, #eef1f6); }
		.acw-grant-add-head { font-size: 0.7rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-secondary, #45505f); margin: 0 0 0.6rem; }
		.acw-grant-add-row { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 0.7rem; }
		.acw-grant-field { display: flex; flex-direction: column; gap: 0.2rem; }
		.acw-grant-flabel { font-size: 0.72rem; font-weight: 600; color: var(--theme-color-text-secondary, #45505f); }
		.acw-grant-picker { min-width: 20rem; }
		.acw-grant-btn { font-size: 0.85rem; font-weight: 600; padding: 0.4rem 0.9rem; border-radius: 7px; border: none; background: var(--theme-color-primary, #2f6fed); color: #fff; cursor: pointer; }
		.acw-grant-busy { font-size: 0.8rem; color: var(--theme-color-primary, #2f6fed); font-weight: 600; }
		.acw-grant-notice { font-size: 0.85rem; color: #276749; background: #e6f4ea; border-radius: 6px; padding: 0.45rem 0.6rem; margin: 0.6rem 0 0; }
		.acw-grant-error { font-size: 0.85rem; color: var(--theme-color-status-danger, #c0392b); margin: 0.5rem 0 0; }
		.acw-matrix-bar { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.75rem; }
		.acw-matrix-search { max-width: 22rem; }
		.acw-matrix-summary { margin-left: auto; display: flex; gap: 0.4rem; }
		.acw-matrix-scroll { overflow-x: auto; border: 1px solid var(--bulma-border, #ededed); border-radius: 6px; }
		.acw-matrix-table { margin-bottom: 0; font-size: 0.82rem; }
		.acw-matrix-table thead th { position: sticky; top: 0; background: var(--bulma-scheme-main, #fff); z-index: 2; border-bottom: 2px solid var(--bulma-border, #dbdbdb); }
		.acw-matrix-action { text-align: center; white-space: nowrap; font-size: 0.72rem; }
		.acw-matrix-entity-col, .acw-matrix-entity { position: sticky; left: 0; background: var(--bulma-scheme-main, #fff); z-index: 1; font-weight: 600; white-space: nowrap; }
		.acw-matrix-entity-col { z-index: 3; }
		.acw-matrix-section td { background: var(--bulma-background, #f5f5f5); font-weight: 700; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--bulma-text-weak, #7a7a7a); }
		.acw-matrix-section-count { color: var(--bulma-text-weak, #9a9a9a); margin-left: 0.3rem; }
		.acw-cell { text-align: center; width: 2.2rem; }
		.acw-cell-on { color: var(--theme-color-status-success, #2e7d32); }
		.acw-cell-off { color: var(--theme-color-status-error, #c0392b); }
		.acw-cell-none { color: var(--bulma-border, #dbdbdb); }
		.acw-cell-na { background: repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.02) 4px, rgba(0,0,0,0.02) 8px); }
		.acw-dash { color: var(--bulma-border, #cfcfcf); }
		.acw-cell-clickable { cursor: pointer; }
		.acw-cell-clickable:hover { background: var(--bulma-background, #f0f0f0); }
		.acw-cell-changed { outline: 2px solid var(--bulma-link, #485fc7); outline-offset: -2px; }
		.acw-faint { opacity: 0.35; }
		.acw-all-col { border-right: 2px solid var(--bulma-border, #dbdbdb); }
		.acw-matrix-mode { margin-bottom: 0 !important; margin-right: 0.5rem; }
		.acw-matrix-mode .button { margin-bottom: 0; }
		.acw-enforce-col { text-align: center; white-space: nowrap; border-left: 2px solid var(--bulma-border, #dbdbdb); padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
		.acw-enforce-head { font-size: 0.72rem; }
		.acw-inert { display: inline-flex; align-items: center; gap: 0.15rem; color: #b06a00; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; margin-left: 0.3rem; white-space: nowrap; }
		.acw-matrix-savebar { display: flex; align-items: center; justify-content: space-between; }
		.acw-matrix-savebar-actions { display: flex; gap: 0.5rem; }
		.acw-roles-impact { display: flex; align-items: center; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 0.75rem; }
		.acw-roles-impact-label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--bulma-text-weak, #7a7a7a); margin-right: 0.2rem; }
		.acw-role-chip { cursor: pointer; border: none; }
		.acw-role-chip-count { margin-left: 0.35rem; font-weight: 700; opacity: 0.7; }
		.acw-cell-override { color: #d9821b; }
		.acw-cell-default { color: #3273dc; }
		.acw-cell-inherited { color: #3273dc; }
		.acw-explain { position: relative; }
		.acw-explain-title { margin-bottom: 0.4rem; }
		.acw-explain-line { margin-bottom: 0.15rem; font-size: 0.85rem; font-family: monospace; }
		.acw-eff-section { border: 1px solid var(--bulma-border, #ededed); border-radius: 8px; margin-bottom: 0.85rem; overflow: hidden; }
		.acw-eff-head { display: flex; align-items: center; gap: 0.5rem; padding: 0.55rem 0.8rem; background: var(--bulma-background, #f7f7f7); flex-wrap: wrap; }
		.acw-eff-head-title { font-weight: 700; }
		.acw-eff-head-counts { margin-left: auto; display: flex; gap: 0.35rem; }
		.acw-eff-members { display: inline-flex; align-items: center; gap: 0.3rem; flex-wrap: wrap; font-size: 0.72rem; color: var(--bulma-text-weak, #7a7a7a); }
		.acw-eff-row { display: flex; align-items: center; gap: 0.6rem; padding: 0.35rem 0.8rem; border-top: 1px solid var(--bulma-border, #f3f3f3); font-size: 0.85rem; }
		.acw-eff-row-status { width: 1.2rem; text-align: center; }
		.acw-eff-row-entity { font-weight: 600; }
		.acw-eff-row-action { color: var(--bulma-text-weak, #7a7a7a); }
		.acw-eff-row-source { margin-left: auto; }
		.acw-eff-why { color: var(--bulma-link, #485fc7); }
		.acw-eff-deny .acw-eff-row-entity { color: var(--theme-color-status-error, #c0392b); }
		.acw-eff-empty { padding: 0.5rem 0.8rem; color: var(--bulma-text-weak, #9a9a9a); font-size: 0.82rem; }
		.acw-rail-actions { display: flex; gap: 0.4rem; margin-bottom: 0.6rem; }
		.acw-details-edit { max-width: 42rem; }
		.acw-details-actions { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.5rem; flex-wrap: wrap; }
		.acw-delete-confirm { display: inline-flex; align-items: center; gap: 0.4rem; }
		.acw-section-help { color: var(--bulma-text-weak, #7a7a7a); margin-bottom: 0.75rem; }
		.acw-add-members { max-width: 42rem; margin-bottom: 1rem; padding: 0.9rem 1rem; }
		.acw-add-members-head { display: flex; align-items: center; gap: 0.4rem; font-weight: 600; margin-bottom: 0.25rem; }
		.acw-add-members-help { color: var(--bulma-text-weak, #7a7a7a); font-size: 0.85rem; margin-bottom: 0.6rem; }
		.acw-add-members-row { display: flex; align-items: flex-start; gap: 0.5rem; }
		.acw-add-members-picker { flex: 1 1 auto; min-width: 0; }
		.acw-add-members-btn { flex: 0 0 auto; }
		.acw-add-members-saving { flex: 0 0 auto; align-self: center; }
		.acw-details-addmembers { margin-top: 1rem; }
		.acw-edge-list { border: 1px solid var(--bulma-border, #ededed); border-radius: 6px; max-width: 42rem; }
		.acw-edge-row { display: flex; align-items: center; padding: 0.4rem 0.6rem; border-bottom: 1px solid var(--bulma-border, #f3f3f3); }
		.acw-edge-row:last-child { border-bottom: none; }
		.acw-edge-main { display: flex; align-items: center; gap: 0.4rem; flex: 1 1 auto; cursor: pointer; color: inherit; }
		.acw-edge-add { display: flex; gap: 0.5rem; margin-top: 0.75rem; align-items: center; flex-wrap: wrap; }
		.acw-member-name { font-weight: 600; }
		.acw-member-sub { font-size: 0.72rem; color: var(--bulma-text-weak, #9a9a9a); margin-left: 0.4rem; }
		.acw-member-levels { display: inline-flex; gap: 0.35rem; align-items: center; margin-left: auto; flex-wrap: wrap; flex: 0 0 auto; }
		.acw-members-count { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--bulma-text-weak, #7a7a7a); margin-bottom: 0.5rem; }
		.acw-assign-form { margin-top: 1rem; max-width: 42rem; }
		.acw-expiration-input { max-width: 11rem; }
		.acw-date-field { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.75rem; color: var(--bulma-text-weak, #7a7a7a); }
		.acw-spinner { display: inline-block; width: 1.4rem; height: 1.4rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #485fc7); border-radius: 50%; animation: acw-spin 0.8s linear infinite; }
		@keyframes acw-spin { to { transform: rotate(360deg); } }
	`,
	Templates:
	[
		...libTabBar.TABBAR_TEMPLATES,
		{
			Hash: 'AccessWorkspace-Wrap',
			Template: /*html*/`
				<div class="acw-wrap">
					<div class="acw-header">
						<div>
							<h1 class="title is-4">Access &amp; Permissions</h1>
							<p class="subtitle is-6 has-text-grey">Roles, permission groups, the permission matrix, and who has what — in one place.</p>
						</div>
						<div class="acw-context">
							<div class="field has-addons">
								<div class="control"><span class="acw-context-label">Context</span></div>
								<div class="control"><div class="select"><select onchange="_Pict.providers.AccessWorkspace.setContext(this.value)">{~TS:AccessWorkspace-ContextOption:AppData.AccessWorkspace.ContextOptions~}</select></div></div>
								{~TS:AccessWorkspace-ContextID:AppData.AccessWorkspace.ContextIDSlot~}
							</div>
						</div>
					</div>
					{~TS:AccessWorkspace-Message:AppData.AccessWorkspace.MessageSlot~}
					<div class="columns">
						<div class="column is-one-third">
							<div class="acw-rail box">
								<input id="acw-rail-search" class="input acw-search" type="text" placeholder="Search roles, groups, users…" value="{~D:AppData.AccessWorkspace.Search~}" oninput="_Pict.providers.AccessWorkspace.setSearch(this.value)" />
								{~TS:AccessWorkspace-RailActions:AppData.AccessWorkspace.NewButtonsSlot~}
								{~TS:AccessWorkspace-RailLoading:AppData.AccessWorkspace.RailLoadingSlot~}
								{~TS:AccessWorkspace-RailGroup:AppData.AccessWorkspace.RailGroups~}
							</div>
						</div>
						<div class="column">
							{~TS:AccessWorkspace-Subject:AppData.AccessWorkspace.SubjectSlot~}
							{~TS:AccessWorkspace-Empty:AppData.AccessWorkspace.EmptySlot~}
						</div>
					</div>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-ContextOption',
			Template: /*html*/`<option value="{~D:Record.Key~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>`,
		},
		{
			Hash: 'AccessWorkspace-ContextID',
			Template: /*html*/`<div class="control"><input class="input" type="number" placeholder="{~D:Record.Context~} ID" value="{~D:Record.ContextIDInput~}" onchange="_Pict.providers.AccessWorkspace.setContextID(this.value)" /></div>`,
		},
		{
			Hash: 'AccessWorkspace-RailLoading',
			Template: /*html*/`<div class="has-text-centered py-4"><span class="acw-spinner"></span><p class="mt-2 has-text-grey is-size-7">Loading…</p></div>`,
		},
		{
			Hash: 'AccessWorkspace-RailGroup',
			Template: /*html*/`
				<div class="acw-rail-group">
					<div class="acw-rail-head"><span class="icon is-small"><i class="{~D:Record.Icon~}"></i></span><span>{~D:Record.Label~}</span><span class="acw-rail-count">{~D:Record.Count~}</span></div>
					{~TS:AccessWorkspace-RailRow:Record.Rows~}
					{~TS:AccessWorkspace-RailHint:Record.HintSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-RailHint',
			Template: /*html*/`<p class="acw-rail-hint has-text-grey is-size-7 px-2 py-2">{~D:Record.Hint~}</p>`,
		},
		{
			Hash: 'AccessWorkspace-RailRow',
			Template: /*html*/`
				<a class="acw-rail-row {~D:Record.RowClass~}" onclick="_Pict.providers.AccessWorkspace.selectSubject('{~D:Record.Kind~}', {~D:Record.ID~})">
					<span class="acw-rail-label">{~D:Record.Label~}</span>
					<span class="acw-rail-sub">{~D:Record.Sub~}</span>
				</a>`,
		},
		{
			Hash: 'AccessWorkspace-Message',
			Template: /*html*/`<div class="notification {~D:Record.MessageClass~} is-soft py-2 px-3 mb-3">{~D:Record.Message~}</div>`,
		},
		{
			Hash: 'AccessWorkspace-Subject',
			Template: /*html*/`
				<div class="acw-subject box">
					<div class="acw-subject-head">
						<span class="icon is-medium"><i class="{~D:Record.SubjectIcon~} fa-lg"></i></span>
						<div>
							<h2 class="title is-5">{~D:Record.SubjectTitle~}</h2>
							<p class="acw-subject-kind">{~D:Record.SubjectKindLabel~}</p>
						</div>
					</div>
					<div class="hltabs hltabs--menu acw-tabs">{~TS:HL-TabBar-Item:Record.Tabs~}</div>
					<div class="acw-tab-body">
						{~TS:AccessWorkspace-DetailsBody:Record.DetailsReadSlot~}
						{~TS:AccessWorkspace-DetailsEdit:Record.DetailsEditSlot~}
						{~TS:AccessWorkspace-Matrix:Record.MatrixActiveSlot~}
						{~TS:AccessWorkspace-Effective:Record.EffectiveActiveSlot~}
						{~TS:AccessWorkspace-Composition:Record.CompositionActiveSlot~}
						{~TS:AccessWorkspace-Members:Record.MembersActiveSlot~}
						{~TS:AccessWorkspace-ProjectsGranted:Record.ProjectsGrantedActiveSlot~}
						{~TS:AccessWorkspace-Assignment:Record.AssignmentActiveSlot~}
						{~TS:AccessWorkspace-Overrides:Record.OverridesActiveSlot~}
						{~TS:AccessWorkspace-Placeholder:Record.PlaceholderActiveSlot~}
					</div>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-DetailsBody',
			Template: /*html*/`
				<table class="table is-fullwidth acw-details">
					<tbody>{~TS:AccessWorkspace-DetailField:Record.Fields~}</tbody>
				</table>`,
		},
		{
			Hash: 'AccessWorkspace-DetailField',
			Template: /*html*/`<tr><th class="acw-detail-label">{~D:Record.Label~}</th><td>{~D:Record.Value~}</td></tr>`,
		},
		{
			Hash: 'AccessWorkspace-Placeholder',
			Template: /*html*/`
				<div class="acw-placeholder has-text-grey has-text-centered">
					<span class="icon is-large"><i class="fas fa-screwdriver-wrench fa-2x"></i></span>
					<p class="mt-3">{~D:Record.Text~}</p>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-Empty',
			Template: /*html*/`
				<div class="acw-empty box has-text-grey has-text-centered">
					<span class="icon is-large"><i class="fas fa-hand-pointer fa-2x"></i></span>
					<p class="mt-3">Pick a role, permission group, or user on the left to manage its access.</p>
				</div>`,
		},
		{
			// User-Group-only "Projects Granted" tab. The body is a host div the UserGroupGrants provider paints
			// from onAfterRender (effective projects/labs + contract grants + add control) — same host-mount idiom
			// as the User workspace Roles panel, so a full re-render never blows away the mounted grant picker.
			Hash: 'AccessWorkspace-ProjectsGranted',
			Template: /*html*/`<div class="acw-grants"><div id="acw-grants-host"></div></div>`,
		},
		{
			Hash: 'AccessWorkspace-Matrix',
			Template: /*html*/`
				<div class="acw-matrix">
					<div class="acw-matrix-bar">
						{~TS:AccessWorkspace-MatrixMode:AppData.AccessWorkspace.Matrix.ModeSlot~}
						<input id="acw-matrix-search-input" class="input acw-matrix-search" type="text" placeholder="Filter…" value="{~D:AppData.AccessWorkspace.Matrix.Search~}" oninput="_Pict.providers.PermissionMatrix.setSearch(this.value)" />
						<span class="acw-matrix-summary"><span class="tag is-success is-light">{~D:AppData.AccessWorkspace.Matrix.GrantCount~} granted</span><span class="tag is-danger is-light">{~D:AppData.AccessWorkspace.Matrix.DenyCount~} denied</span></span>
					</div>
					{~TS:AccessWorkspace-MatrixEnforceBanner:AppData.AccessWorkspace.Matrix.EnforcementBannerSlot~}
					{~TS:AccessWorkspace-MatrixEditHint:AppData.AccessWorkspace.Matrix.EditHintSlot~}
					{~TS:AccessWorkspace-MatrixSaveBar:AppData.AccessWorkspace.Matrix.SaveBarSlot~}
					{~TS:AccessWorkspace-MatrixNotice:AppData.AccessWorkspace.Matrix.SaveNoticeSlot~}
					{~TS:AccessWorkspace-MatrixLoading:AppData.AccessWorkspace.Matrix.LoadingSlot~}
					{~TS:AccessWorkspace-MatrixError:AppData.AccessWorkspace.Matrix.ErrorSlot~}
					{~TS:AccessWorkspace-MatrixGrid:AppData.AccessWorkspace.Matrix.GridSlot~}
					{~TS:AccessWorkspace-MatrixNoMatch:AppData.AccessWorkspace.Matrix.NoMatchSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixGrid',
			Template: /*html*/`
				<div class="acw-matrix-scroll">
					<table class="table is-narrow acw-matrix-table">
						<thead><tr><th class="acw-matrix-entity-col">Entity</th>{~TS:AccessWorkspace-MatrixAllHead:AppData.AccessWorkspace.Matrix.AllHeadSlot~}{~TS:AccessWorkspace-MatrixHead:AppData.AccessWorkspace.Matrix.Actions~}{~TS:AccessWorkspace-MatrixEnforceHead:AppData.AccessWorkspace.Matrix.EnforcementHeadSlot~}</tr></thead>
						<tbody>{~TS:AccessWorkspace-MatrixSection:AppData.AccessWorkspace.Matrix.Sections~}</tbody>
					</table>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixHead',
			Template: /*html*/`<th class="acw-matrix-action">{~D:Record.Action~}</th>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixSection',
			Template: /*html*/`
				<tr class="acw-matrix-section"><td colspan="20"><span class="icon is-small"><i class="fas fa-layer-group"></i></span> {~D:Record.Title~}<span class="acw-matrix-section-count">{~D:Record.RowCount~}</span></td></tr>
				{~TS:AccessWorkspace-MatrixRow:Record.Rows~}`,
		},
		{
			Hash: 'AccessWorkspace-MatrixRow',
			Template: /*html*/`<tr><td class="acw-matrix-entity">{~D:Record.Entity~}</td>{~TS:AccessWorkspace-MatrixAllCell:Record.AllSlot~}{~TS:AccessWorkspace-MatrixCell:Record.Cells~}{~TS:AccessWorkspace-MatrixEnforceCell:Record.EnforceSlot~}</tr>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixCell',
			Template: /*html*/`<td class="{~D:Record.CellClass~}" title="{~D:Record.Title~}" onclick="_Pict.providers.PermissionMatrix.toggleCell('{~D:Record.Hash~}')">{~D:Record.Icon~}</td>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixLoading',
			Template: /*html*/`<div class="has-text-centered py-5"><span class="acw-spinner"></span><p class="mt-2 has-text-grey">Loading permissions…</p></div>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixError',
			Template: /*html*/`<div class="notification is-warning is-soft">{~D:Record.Error~}</div>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixNoMatch',
			Template: /*html*/`<div class="acw-placeholder has-text-grey has-text-centered"><p>No entities match your filter.</p></div>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixAllHead',
			Template: /*html*/`<th class="acw-matrix-action acw-all-col" title="Set all actions for the entity">All</th>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixAllCell',
			Template: /*html*/`<td class="{~D:Record.CellClass~} acw-all-col" title="{~D:Record.Title~}" onclick="_Pict.providers.PermissionMatrix.toggleAll('{~D:Record.Entity~}')">{~D:Record.Icon~}</td>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixMode',
			Template: /*html*/`
				<div class="buttons has-addons acw-matrix-mode">
					<button class="button is-small {~D:Record.EntitiesClass~}" onclick="_Pict.providers.PermissionMatrix.setCatalogMode('entities')"><span class="icon is-small"><i class="fas fa-table-cells"></i></span><span>Entities</span></button>
					<button class="button is-small {~D:Record.ReportsClass~}" onclick="_Pict.providers.PermissionMatrix.setCatalogMode('reports')"><span class="icon is-small"><i class="fas fa-file-lines"></i></span><span>Reports &amp; Forms</span></button>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixEnforceBanner',
			Template: /*html*/`<div class="notification is-warning is-soft py-2 px-3"><span class="icon"><i class="fas fa-triangle-exclamation"></i></span> <span>{~D:Record.Text~}</span></div>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixEnforceHead',
			Template: /*html*/`<th class="acw-matrix-action acw-enforce-col acw-enforce-head" title="Whether grants for a report/form are actually enforced (PermissionsEnforcement)">Enforce</th>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixEnforceCell',
			Template: /*html*/`<td class="acw-enforce-col">{~TS:AccessWorkspace-MatrixEnforceEdit:Record.EditSlot~}{~TS:AccessWorkspace-MatrixEnforceRead:Record.ReadSlot~}{~TS:AccessWorkspace-MatrixEnforceInert:Record.InertSlot~}</td>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixEnforceEdit',
			Template: /*html*/`<div class="select is-small"><select onchange="_Pict.providers.PermissionMatrix.setEnforcementModeAsync('{~D:Record.RNIHash~}', this.value)">{~TS:AccessWorkspace-MatrixEnforceOption:Record.Options~}</select></div>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixEnforceOption',
			Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixEnforceRead',
			Template: /*html*/`<span class="tag {~D:Record.BadgeClass~}" title="{~D:Record.Title~}">{~D:Record.Mode~}</span>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixEnforceInert',
			Template: /*html*/`<span class="acw-inert" title="Grants for this report are not enforced (mode Default) — they will not restrict access."><span class="icon is-small"><i class="fas fa-triangle-exclamation"></i></span>inert</span>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixEditHint',
			Template: /*html*/`<div class="notification is-warning is-soft py-2 px-3"><span class="icon"><i class="fas fa-lock"></i></span> <span>{~D:Record.Text~}</span></div>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixSaveBar',
			Template: /*html*/`
				<div class="acw-matrix-savebar notification is-link is-soft py-2 px-3">
					<span><strong>{~D:Record.EditCount~}</strong> unsaved change(s)</span>
					<span class="acw-matrix-savebar-actions">
						<button class="button is-small" onclick="_Pict.providers.PermissionMatrix.discard()">Discard</button>
						<button class="button is-small is-link" {~D:Record.SaveDisabledAttr~} onclick="_Pict.providers.PermissionMatrix.saveAsync()"><span class="icon"><i class="fas fa-floppy-disk"></i></span><span>Save changes</span></button>
					</span>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-MatrixNotice',
			Template: /*html*/`<div class="notification {~D:Record.Class~} py-2 px-3"><button class="delete" onclick="_Pict.providers.PermissionMatrix.dismissNotice()"></button>{~D:Record.Text~}</div>`,
		},
		{
			Hash: 'AccessWorkspace-Effective',
			Template: /*html*/`
				<div class="acw-effective">
					<div class="acw-matrix-bar">
						<input id="acw-effective-search-input" class="input acw-matrix-search" type="text" placeholder="Filter permissions…" value="{~D:AppData.AccessWorkspace.Effective.Search~}" oninput="_Pict.providers.PermissionVisualizer.setSearch(this.value)" />
						<span class="acw-matrix-summary">{~TS:AccessWorkspace-EffectiveSummary:AppData.AccessWorkspace.Effective.SummarySlot~}</span>
					</div>
					{~TS:AccessWorkspace-EffectiveExplain:AppData.AccessWorkspace.Effective.ExplainSlot~}
					{~TS:AccessWorkspace-MatrixLoading:AppData.AccessWorkspace.Effective.LoadingSlot~}
					{~TS:AccessWorkspace-MatrixError:AppData.AccessWorkspace.Effective.ErrorSlot~}
					{~TS:AccessWorkspace-EffSection:AppData.AccessWorkspace.Effective.Sections~}
					{~TS:AccessWorkspace-MatrixNoMatch:AppData.AccessWorkspace.Effective.NoMatchSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-EffSection',
			Template: /*html*/`
				<div class="acw-eff-section">
					<div class="acw-eff-head">
						<span class="icon is-small"><i class="fas fa-location-dot"></i></span>
						<span class="acw-eff-head-title">{~D:Record.Label~}</span>
						{~TS:AccessWorkspace-EffMembers:Record.MemberSlot~}
						{~TS:AccessWorkspace-EffSuper:Record.SuperSlot~}
						<span class="acw-eff-head-counts"><span class="tag is-success is-light">{~D:Record.Allowed~} granted</span><span class="tag is-danger is-light">{~D:Record.Denied~} denied</span></span>
					</div>
					<div class="acw-eff-rows">
						{~TS:AccessWorkspace-EffRow:Record.Rows~}
						{~TS:AccessWorkspace-EffEmpty:Record.EmptySlot~}
					</div>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-EffMembers',
			Template: /*html*/`<span class="acw-eff-members">via {~TS:AccessWorkspace-EffMemberChip:Record.Members~}</span>`,
		},
		{
			Hash: 'AccessWorkspace-EffMemberChip',
			Template: /*html*/`<button class="tag is-info is-light acw-role-chip" onclick="_Pict.providers.PermissionVisualizer.selectRole({~D:Record.IDRole~})" title="Jump to this role">{~D:Record.Label~}</button>`,
		},
		{
			Hash: 'AccessWorkspace-EffSuper',
			Template: /*html*/`<span class="tag is-warning">super-user — all by default</span>`,
		},
		{
			Hash: 'AccessWorkspace-EffRow',
			Template: /*html*/`
				<div class="acw-eff-row {~D:Record.RowClass~}">
					<span class="acw-eff-row-status">{~D:Record.StatusIcon~}</span>
					<span class="acw-eff-row-hash"><span class="acw-eff-row-entity">{~D:Record.Entity~}</span> · <span class="acw-eff-row-action">{~D:Record.Action~}</span></span>
					<span class="acw-eff-row-source"><span class="tag {~D:Record.SourceClass~}">{~D:Record.SourceLabel~}</span></span>
					{~TS:AccessWorkspace-EffWhy:Record.WhySlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-EffWhy',
			Template: /*html*/`<button class="button is-small is-white acw-eff-why" title="Why?" onclick="_Pict.providers.PermissionVisualizer.explainAsync('{~D:Record.Hash~}','{~D:Record.Context~}',{~D:Record.IDContext~})"><span class="icon is-small"><i class="fas fa-circle-question"></i></span></button>`,
		},
		{
			Hash: 'AccessWorkspace-EffEmpty',
			Template: /*html*/`<div class="acw-eff-empty">No explicit permissions in this context.</div>`,
		},
		{
			Hash: 'AccessWorkspace-EffectiveSummary',
			Template: /*html*/`<span class="tag is-info is-light">{~D:Record.Text~}</span>`,
		},
		{
			Hash: 'AccessWorkspace-EffectiveRolesImpact',
			Template: /*html*/`
				<div class="acw-roles-impact">
					<span class="acw-roles-impact-label">Roles in effect:</span>
					{~TS:AccessWorkspace-EffectiveRoleChip:AppData.AccessWorkspace.Effective.RolesImpact~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-EffectiveRoleChip',
			Template: /*html*/`<button class="tag acw-role-chip {~D:Record.ChipClass~}" onclick="_Pict.providers.PermissionVisualizer.selectRole({~D:Record.IDRole~})" title="Jump to this role">{~D:Record.Label~} <span class="acw-role-chip-count">{~D:Record.Count~}</span></button>`,
		},
		{
			Hash: 'AccessWorkspace-EffectiveGrid',
			Template: /*html*/`
				<div class="acw-matrix-scroll">
					<table class="table is-narrow acw-matrix-table">
						<thead><tr><th class="acw-matrix-entity-col">Entity</th>{~TS:AccessWorkspace-MatrixHead:AppData.AccessWorkspace.Effective.Actions~}</tr></thead>
						<tbody>{~TS:AccessWorkspace-EffectiveSection:AppData.AccessWorkspace.Effective.Sections~}</tbody>
					</table>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-EffectiveSection',
			Template: /*html*/`
				<tr class="acw-matrix-section"><td colspan="20"><span class="icon is-small"><i class="fas fa-layer-group"></i></span> {~D:Record.Title~}<span class="acw-matrix-section-count">{~D:Record.RowCount~}</span></td></tr>
				{~TS:AccessWorkspace-EffectiveRow:Record.Rows~}`,
		},
		{
			Hash: 'AccessWorkspace-EffectiveRow',
			Template: /*html*/`<tr><td class="acw-matrix-entity">{~D:Record.Entity~}</td>{~TS:AccessWorkspace-EffectiveCell:Record.Cells~}</tr>`,
		},
		{
			Hash: 'AccessWorkspace-EffectiveCell',
			Template: /*html*/`<td class="{~D:Record.CellClass~}" title="{~D:Record.Title~}" onclick="_Pict.providers.PermissionVisualizer.explainAsync('{~D:Record.Hash~}')">{~D:Record.Icon~}</td>`,
		},
		{
			Hash: 'AccessWorkspace-EffectiveExplain',
			Template: /*html*/`
				<div class="acw-explain notification is-soft">
					<button class="delete" onclick="_Pict.providers.PermissionVisualizer.closeExplain()"></button>
					<p class="acw-explain-title"><strong>Why?</strong> <code>{~D:Record.Hash~}</code></p>
					{~TS:AccessWorkspace-ExplainLoading:Record.LoadingSlot~}
					{~TS:AccessWorkspace-ExplainError:Record.ErrorSlot~}
					{~TS:AccessWorkspace-ExplainLine:Record.Lines~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-ExplainLoading',
			Template: /*html*/`<p class="has-text-grey"><span class="acw-spinner"></span> Resolving…</p>`,
		},
		{
			Hash: 'AccessWorkspace-ExplainError',
			Template: /*html*/`<p class="has-text-danger">{~D:Record.Error~}</p>`,
		},
		{
			Hash: 'AccessWorkspace-ExplainLine',
			Template: /*html*/`<p class="acw-explain-line {~D:Record.Class~}">{~D:Record.Text~}</p>`,
		},
		{
			Hash: 'AccessWorkspace-RailActions',
			Template: /*html*/`
				<div class="acw-rail-actions">
					<button class="button is-small is-link is-light" onclick="_Pict.providers.AccessWorkspace.newRole()"><span class="icon is-small"><i class="fas fa-plus"></i></span><span>Role</span></button>
					<button class="button is-small is-link is-light" onclick="_Pict.providers.AccessWorkspace.newGroup()"><span class="icon is-small"><i class="fas fa-plus"></i></span><span>Group</span></button>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-DetailsEdit',
			Template: /*html*/`
				<div class="acw-details-edit">
					<div class="field"><label class="label is-small">Name</label><div class="control"><input id="acw-detail-name" class="input" type="text" value="{~D:Record.Name~}" oninput="_Pict.providers.AccessWorkspace.setDetailField('Name', this.value)" /></div></div>
					<div class="columns">
						<div class="column"><div class="field"><label class="label is-small">Type</label><div class="control"><div class="select is-fullwidth"><select onchange="_Pict.providers.AccessWorkspace.setDetailField('Type', this.value)">{~TS:AccessWorkspace-DetailTypeOption:Record.TypeOptions~}</select></div></div></div></div>
						<div class="column"><div class="field"><label class="label is-small">Permission level</label><div class="control"><input id="acw-detail-permlevel" class="input" type="text" placeholder="(optional)" value="{~D:Record.PermissionLevel~}" oninput="_Pict.providers.AccessWorkspace.setDetailField('PermissionLevel', this.value)" /></div></div></div>
					</div>
					<div class="field"><label class="label is-small">Description</label><div class="control"><textarea id="acw-detail-description" class="textarea" rows="2" oninput="_Pict.providers.AccessWorkspace.setDetailField('Description', this.value)">{~D:Record.Description~}</textarea></div></div>
					<p class="help mb-3">Scope: {~D:Record.Scope~}</p>
					<div class="acw-details-actions">
						<button class="button is-link" {~D:Record.SaveDisabledAttr~} onclick="_Pict.providers.AccessWorkspace.saveDetailsAsync()"><span class="icon"><i class="fas fa-floppy-disk"></i></span><span>Save</span></button>
						{~TS:AccessWorkspace-DetailDuplicate:Record.DuplicateButtonSlot~}
						{~TS:AccessWorkspace-DetailDelete:Record.DeleteButtonSlot~}
						{~TS:AccessWorkspace-DetailDeleteConfirm:Record.DeleteConfirmSlot~}
					</div>
					{~TS:AccessWorkspace-DetailsAddMembers:Record.AddMembersSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-DetailsAddMembers',
			Template: /*html*/`
				<div class="acw-add-members box acw-details-addmembers">
					<div class="acw-add-members-head"><span class="icon is-small"><i class="fas fa-user-plus"></i></span><span>Add members</span></div>
					<p class="acw-add-members-help">Search for users and add them to this role or group — many at once.</p>
					<div class="acw-add-members-row">
						<div id="acw-details-add-members-picker" class="acw-add-members-picker"></div>
						{~TS:AccessWorkspace-DetailsAddButton:Record.AddButtonSlot~}
						{~TS:AccessWorkspace-AddMembersSaving:Record.SavingSlot~}
					</div>
					{~TS:AccessWorkspace-DetailsAddNewHint:Record.NewHintSlot~}
					{~TS:AccessWorkspace-AddMembersNotice:Record.NoticeSlot~}
					{~TS:AccessWorkspace-AddMembersError:Record.ErrorSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-DetailsAddButton',
			Template: /*html*/`<button class="button is-link is-small acw-add-members-btn" onclick="_Pict.providers.AccessWorkspace.addDetailsMembersAsync()">Add selected</button>`,
		},
		{
			Hash: 'AccessWorkspace-DetailsAddNewHint',
			Template: /*html*/`<p class="help mt-2 mb-0">The users you pick here will be added to the role when you click <strong>Save</strong>.</p>`,
		},
		{
			Hash: 'AccessWorkspace-DetailTypeOption',
			Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>`,
		},
		{
			Hash: 'AccessWorkspace-DetailDuplicate',
			Template: /*html*/`<button class="button is-light" title="Clone this role into a new one" onclick="window.location.hash = '#/RoleClone/{~D:Record.IDRole~}'"><span class="icon"><i class="fas fa-clone"></i></span><span>Duplicate</span></button>`,
		},
		{
			Hash: 'AccessWorkspace-DetailDelete',
			Template: /*html*/`<button class="button is-danger is-light" onclick="_Pict.providers.AccessWorkspace.requestDelete()"><span class="icon"><i class="fas fa-trash"></i></span><span>Delete</span></button>`,
		},
		{
			Hash: 'AccessWorkspace-DetailDeleteConfirm',
			Template: /*html*/`<span class="acw-delete-confirm"><span class="has-text-danger">Delete this for good?</span><button class="button is-small is-danger" onclick="_Pict.providers.AccessWorkspace.confirmDeleteAsync()">Yes, delete</button><button class="button is-small" onclick="_Pict.providers.AccessWorkspace.cancelDelete()">Cancel</button></span>`,
		},
		{
			Hash: 'AccessWorkspace-Composition',
			Template: /*html*/`
				<div class="acw-composition">
					<p class="acw-section-help">Permission groups and roles included here — their permissions are inherited by this role.</p>
					{~TS:AccessWorkspace-MatrixError:AppData.AccessWorkspace.Composition.ErrorSlot~}
					{~TS:AccessWorkspace-CompositionLoading:AppData.AccessWorkspace.Composition.LoadingSlot~}
					{~TS:AccessWorkspace-CompositionChildren:AppData.AccessWorkspace.Composition.ChildrenSlot~}
					{~TS:AccessWorkspace-CompositionEmpty:AppData.AccessWorkspace.Composition.ChildEmptySlot~}
					{~TS:AccessWorkspace-CompositionAdd:AppData.AccessWorkspace.Composition.AddSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-CompositionLoading',
			Template: /*html*/`<div class="has-text-centered py-4"><span class="acw-spinner"></span></div>`,
		},
		{
			Hash: 'AccessWorkspace-CompositionChildren',
			Template: /*html*/`<div class="acw-edge-list">{~TS:AccessWorkspace-CompositionChildRow:AppData.AccessWorkspace.Composition.Children~}</div>`,
		},
		{
			Hash: 'AccessWorkspace-CompositionChildRow',
			Template: /*html*/`
				<div class="acw-edge-row">
					<a class="acw-edge-main" onclick="_Pict.providers.GroupComposition.selectRole({~D:Record.IDRole~})"><span class="icon"><i class="{~D:Record.Icon~}"></i></span><span>{~D:Record.Label~}</span><span class="tag is-light ml-2">{~D:Record.KindLabel~}</span></a>
					{~TS:AccessWorkspace-CompositionRemove:Record.RemoveSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-CompositionRemove',
			Template: /*html*/`<button class="button is-small is-white" title="Remove" onclick="_Pict.providers.GroupComposition.removeChildAsync({~D:Record.IDRoleEdge~})"><span class="icon"><i class="fas fa-times"></i></span></button>`,
		},
		{
			Hash: 'AccessWorkspace-CompositionEmpty',
			Template: /*html*/`<p class="has-text-grey">No permission groups or roles are included yet.</p>`,
		},
		{
			Hash: 'AccessWorkspace-CompositionAdd',
			Template: /*html*/`
				<div class="acw-edge-add">
					<div class="select is-small"><select onchange="_Pict.providers.GroupComposition.setAddPicker(this.value)">{~TS:AccessWorkspace-CompositionCandidate:AppData.AccessWorkspace.Composition.CandidateOptions~}</select></div>
					<button class="button is-small is-link" onclick="_Pict.providers.GroupComposition.addChildAsync()"><span class="icon is-small"><i class="fas fa-plus"></i></span><span>Include</span></button>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-CompositionCandidate',
			Template: /*html*/`<option value="{~D:Record.IDRole~}">{~D:Record.Label~}</option>`,
		},
		{
			Hash: 'AccessWorkspace-Members',
			Template: /*html*/`
				<div class="acw-members">
					<p class="acw-section-help">People who hold this role or group — resolved the same way as the Home app, including anyone who inherits it through a role that composes it. Each is tagged with whether it applies at the User (customer-wide) level or on specific Projects.</p>
					{~TS:AccessWorkspace-AddMembers:AppData.AccessWorkspace.Membership.AddSlot~}
					{~TS:AccessWorkspace-MembersError:AppData.AccessWorkspace.Membership.ErrorSlot~}
					{~TS:AccessWorkspace-CompositionLoading:AppData.AccessWorkspace.Membership.LoadingSlot~}
					{~TS:AccessWorkspace-MembersList:AppData.AccessWorkspace.Membership.MembersSlot~}
					{~TS:AccessWorkspace-MembersEmpty:AppData.AccessWorkspace.Membership.MemberEmptySlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-AddMembers',
			Template: /*html*/`
				<div class="acw-add-members box">
					<div class="acw-add-members-head"><span class="icon is-small"><i class="fas fa-user-plus"></i></span><span>Add users to this role or group</span></div>
					<p class="acw-add-members-help">Search for users and add them all at once — they'll hold this role at the current context (the context bar above). This replaces adding them one user at a time.</p>
					<div class="acw-add-members-row">
						<div id="acw-add-members-picker" class="acw-add-members-picker"></div>
						<button class="button is-link is-small acw-add-members-btn" onclick="_Pict.providers.GroupComposition.addMembersAsync()">Add selected</button>
						{~TS:AccessWorkspace-AddMembersSaving:AppData.AccessWorkspace.Membership.AddSavingSlot~}
					</div>
					{~TS:AccessWorkspace-AddMembersNotice:AppData.AccessWorkspace.Membership.AddNoticeSlot~}
					{~TS:AccessWorkspace-AddMembersError:AppData.AccessWorkspace.Membership.AddErrorSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-AddMembersSaving',
			Template: /*html*/`<span class="acw-add-members-saving has-text-grey is-size-7">assigning…</span>`,
		},
		{
			Hash: 'AccessWorkspace-AddMembersNotice',
			Template: /*html*/`<div class="notification is-success is-soft py-2 px-3 mt-2 mb-0">{~D:Record.Notice~}</div>`,
		},
		{
			Hash: 'AccessWorkspace-AddMembersError',
			Template: /*html*/`<div class="notification is-warning is-soft py-2 px-3 mt-2 mb-0">{~D:Record.Error~}</div>`,
		},
		{
			Hash: 'AccessWorkspace-MembersError',
			Template: /*html*/`<div class="notification is-warning is-soft">{~D:Record.Error~}</div>`,
		},
		{
			Hash: 'AccessWorkspace-MembersList',
			Template: /*html*/`
				<p class="acw-members-count">{~D:AppData.AccessWorkspace.Membership.CountLabel~}</p>
				<div class="acw-edge-list">{~TS:AccessWorkspace-MemberRow:AppData.AccessWorkspace.Membership.MembersRender~}</div>`,
		},
		{
			Hash: 'AccessWorkspace-MemberRow',
			Template: /*html*/`
				<div class="acw-edge-row">
					<span class="acw-edge-main">
						<span class="icon"><i class="fas fa-user"></i></span>
						<span class="acw-member-name">{~D:Record.Label~}</span>
						<span class="acw-member-sub">{~D:Record.Sub~}</span>
						{~TS:AccessWorkspace-MemberVia:Record.ViaSlot~}
					</span>
					<span class="acw-member-levels">
						{~TS:AccessWorkspace-MemberUserLevel:Record.UserLevelSlot~}
						{~TS:AccessWorkspace-MemberProjectLevel:Record.ProjectLevelSlot~}
						{~TS:AccessWorkspace-MemberRemove:Record.RemoveSlot~}
					</span>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-MemberRemove',
			Template: /*html*/`<button class="button is-small is-white" title="Remove this user from the role or group" onclick="_Pict.providers.GroupComposition.removeMemberAsync({~D:Record.IDUser~})"><span class="icon"><i class="fas fa-times"></i></span></button>`,
		},
		{
			Hash: 'AccessWorkspace-MemberUserLevel',
			Template: /*html*/`<span class="tag is-link is-light" title="Assigned customer-wide via a User Role (UserRoleJoin)"><span class="icon is-small"><i class="fas fa-user-tag"></i></span><span>User level</span></span>`,
		},
		{
			Hash: 'AccessWorkspace-MemberProjectLevel',
			Template: /*html*/`<span class="tag is-info is-light" title="Assigned on one or more Projects (ProjectUserJoin / UserContextRole)"><span class="icon is-small"><i class="fas fa-diagram-project"></i></span><span>{~D:Record.CountLabel~}</span></span>`,
		},
		{
			Hash: 'AccessWorkspace-MemberVia',
			Template: /*html*/`<span class="tag is-light ml-2" title="Inherited because this role is composed into: {~D:Record.ViaLabel~}">via {~D:Record.ViaLabel~}</span>`,
		},
		{
			Hash: 'AccessWorkspace-MembersEmpty',
			Template: /*html*/`<p class="has-text-grey">No users hold this role at the User or Project level.</p>`,
		},
		{
			Hash: 'AccessWorkspace-Assignment',
			Template: /*html*/`
				<div class="acw-assignment">
					<p class="acw-section-help">Roles and permission groups this user holds, by context. This is separate from the base role level on the Details tab.</p>
					{~TS:AccessWorkspace-MatrixError:AppData.AccessWorkspace.Assignment.ErrorSlot~}
					{~TS:AccessWorkspace-CompositionLoading:AppData.AccessWorkspace.Assignment.LoadingSlot~}
					{~TS:AccessWorkspace-AssignmentList:AppData.AccessWorkspace.Assignment.MembershipsSlot~}
					{~TS:AccessWorkspace-MembershipEmpty:AppData.AccessWorkspace.Assignment.MembershipEmptySlot~}
					{~TS:AccessWorkspace-AssignForm:AppData.AccessWorkspace.Assignment.AssignSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-AssignmentList',
			Template: /*html*/`<div class="acw-edge-list">{~TS:AccessWorkspace-MembershipRow:AppData.AccessWorkspace.Assignment.Memberships~}</div>`,
		},
		{
			Hash: 'AccessWorkspace-MembershipRow',
			Template: /*html*/`
				<div class="acw-edge-row">
					<span class="acw-edge-main"><span class="icon"><i class="{~D:Record.Icon~}"></i></span><span>{~D:Record.Label~}</span><span class="tag is-light ml-2">{~D:Record.KindLabel~}</span>
						<span class="tag is-info is-light ml-1">{~D:Record.ContextLabel~}</span>{~TS:AccessWorkspace-MembershipState:Record.StateSlot~}</span>
					{~TS:AccessWorkspace-MembershipRemove:Record.RemoveSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-MembershipState',
			Template: /*html*/`<span class="tag {~D:Record.Class~} is-light ml-1" title="{~D:Record.Title~}">{~D:Record.Label~}</span>`,
		},
		{
			Hash: 'AccessWorkspace-MembershipRemove',
			Template: /*html*/`<button class="button is-small is-white" title="Remove" onclick="_Pict.providers.RoleAssignment.removeMembershipAsync({~D:Record.IDRole~}, '{~D:Record.Context~}', {~D:Record.IDContext~})"><span class="icon"><i class="fas fa-times"></i></span></button>`,
		},
		{
			Hash: 'AccessWorkspace-MembershipEmpty',
			Template: /*html*/`<p class="has-text-grey">This user holds no rich roles yet — assign one below, or see their base level on Details.</p>`,
		},
		{
			Hash: 'AccessWorkspace-AssignForm',
			Template: /*html*/`
				<div class="acw-assign-form box">
					<p class="label is-small">Assign a role/group in {~D:Record.Context~}</p>
					<div class="acw-edge-add">
						<div class="select is-small"><select onchange="_Pict.providers.RoleAssignment.setAssignRole(this.value)">{~TS:AccessWorkspace-AssignRoleOption:AppData.AccessWorkspace.Assignment.RoleOptions~}</select></div>
						{~TS:AccessWorkspace-AssignExpiration:AppData.AccessWorkspace.Assignment.ExpirationSlot~}
						<button class="button is-small is-link" onclick="_Pict.providers.RoleAssignment.assignAsync()"><span class="icon is-small"><i class="fas fa-plus"></i></span><span>Assign</span></button>
					</div>
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-AssignRoleOption',
			Template: /*html*/`<option value="{~D:Record.IDRole~}">{~D:Record.Label~}</option>`,
		},
		{
			Hash: 'AccessWorkspace-AssignExpiration',
			Template: /*html*/`
				<label class="acw-date-field">Starts<input class="input is-small acw-expiration-input" type="date" title="Optional first day" min="{~D:Record.Today~}" value="{~D:Record.StartDraft~}" onchange="_Pict.providers.RoleAssignment.setStart(this.value)" /></label>
				<label class="acw-date-field">Ends<input class="input is-small acw-expiration-input" type="date" title="Optional last day" min="{~D:Record.Today~}" value="{~D:Record.ExpirationDraft~}" onchange="_Pict.providers.RoleAssignment.setExpiration(this.value)" /></label>`,
		},
		{
			Hash: 'AccessWorkspace-Overrides',
			Template: /*html*/`
				<div class="acw-overrides">
					<p class="acw-section-help">Direct permission overrides for this user in the current context — these win over any role. Use exact hashes like <code>Project-Read</code>.</p>
					{~TS:AccessWorkspace-MatrixError:AppData.AccessWorkspace.Assignment.ErrorSlot~}
					{~TS:AccessWorkspace-CompositionLoading:AppData.AccessWorkspace.Assignment.LoadingSlot~}
					{~TS:AccessWorkspace-OverridesList:AppData.AccessWorkspace.Assignment.OverridesSlot~}
					{~TS:AccessWorkspace-OverridesEmpty:AppData.AccessWorkspace.Assignment.OverrideEmptySlot~}
					{~TS:AccessWorkspace-OverrideForm:AppData.AccessWorkspace.Assignment.AssignSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-OverridesList',
			Template: /*html*/`<div class="acw-edge-list">{~TS:AccessWorkspace-OverrideRow:AppData.AccessWorkspace.Assignment.Overrides~}</div>`,
		},
		{
			Hash: 'AccessWorkspace-OverrideRow',
			Template: /*html*/`
				<div class="acw-edge-row">
					<span class="acw-edge-main"><code>{~D:Record.Hash~}</code><span class="tag {~D:Record.AllowClass~} is-light ml-2">{~D:Record.AllowLabel~}</span></span>
					{~TS:AccessWorkspace-OverrideRemove:Record.RemoveSlot~}
				</div>`,
		},
		{
			Hash: 'AccessWorkspace-OverrideRemove',
			Template: /*html*/`<button class="button is-small is-white" title="Remove" onclick="_Pict.providers.RoleAssignment.removeOverrideAsync({~D:Record.IDUserPermission~})"><span class="icon"><i class="fas fa-times"></i></span></button>`,
		},
		{
			Hash: 'AccessWorkspace-OverridesEmpty',
			Template: /*html*/`<p class="has-text-grey">No direct overrides in this context.</p>`,
		},
		{
			Hash: 'AccessWorkspace-OverrideForm',
			Template: /*html*/`
				<div class="acw-assign-form box">
					<div class="acw-edge-add">
						<input class="input is-small" type="text" placeholder="Permission hash (e.g. Project-Read)" value="{~D:AppData.AccessWorkspace.Assignment.OverrideHash~}" oninput="_Pict.providers.RoleAssignment.setOverrideHash(this.value)" />
						<div class="select is-small"><select onchange="_Pict.providers.RoleAssignment.setOverrideAllow(this.value)"><option value="on">Allow</option><option value="off">Deny</option></select></div>
						<button class="button is-small is-link" onclick="_Pict.providers.RoleAssignment.addOverrideAsync()"><span class="icon is-small"><i class="fas fa-plus"></i></span><span>Add override</span></button>
					</div>
				</div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'AccessWorkspace-Wrap',
			TemplateHash: 'AccessWorkspace-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container',
		},
	],
};
